import { useEffect, useId, useRef, useState } from "react";
import { Archive, Columns2, MoreHorizontal, Square } from "lucide-react";

interface Props {
  /** Current split-view state — drives the segmented "Vue" toggle. */
  splitView: boolean;
  setSplitView: (next: boolean) => void;
  /** Number of archived panneaux. The kebab badge dot only renders when > 0,
   *  and the "Panneaux archivés" menu row only shows when > 0. */
  archivedCount: number;
  /** Open the existing ArchivedPanneauxModal. */
  onOpenArchived: () => void;
}

/**
 * Kebab-trigger menu sitting next to `+ Panneau` in the header.
 *
 * Why a menu and not standalone chips:
 * Split-view + Archivés used to live as separate chips on the right
 * side of the action bar — visually disconnected from + Panneau even
 * though all three act on the same object (panneaux). Tucking them
 * here clusters the panneau-management surface and frees two slots
 * from the right cluster.
 *
 * Discoverability is preserved by:
 *   - sitting *immediately adjacent* to + Panneau (the eye lands here
 *     after the most-clicked button)
 *   - a chip-orange dot on the kebab when archivedCount > 0 (signals
 *     "there's something interesting in here")
 */
export function PanneauActionsMenu({
  splitView,
  setSplitView,
  archivedCount,
  onOpenArchived,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();

  // Click-outside + Escape close — same DOM pattern as UserMenu.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const hasArchived = archivedCount > 0;

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        title="Options du panneau"
        aria-label="Options du panneau"
        className="relative inline-flex items-center justify-center min-h-9 min-w-9 px-2 py-2 rounded-full bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 hover:ring-ink/40 dark:hover:ring-white/40 text-ink dark:text-ink-dark"
      >
        <MoreHorizontal className="h-4 w-4" />
        {hasArchived && (
          <span
            aria-hidden
            className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-chip-orange ring-2 ring-canvas dark:ring-canvas-dark"
            title={`${archivedCount} panneau(x) archivé(s)`}
          />
        )}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          className="absolute left-0 mt-2 w-64 rounded-xl bg-white dark:bg-surface-dark ring-1 ring-black/10 dark:ring-white/10 shadow-lg overflow-hidden z-20"
        >
          {/* Vue — segmented toggle, both states visible.
              Same shape as ThemeSegmented in UserMenu so the menu
              UX feels consistent across the app. */}
          <div className="px-3 pt-2.5 pb-2">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted dark:text-muted-dark mb-1.5">
              Vue
            </div>
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-canvas dark:bg-canvas-dark/60 p-0.5">
              {[
                { value: false, label: "Une colonne", Icon: Square },
                { value: true, label: "Côte-à-côte", Icon: Columns2 },
              ].map(({ value, label, Icon }) => {
                const active = splitView === value;
                return (
                  <button
                    key={String(value)}
                    type="button"
                    onClick={() => setSplitView(value)}
                    aria-pressed={active}
                    className={`flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-md text-xs transition ${
                      active
                        ? "bg-white dark:bg-surface-dark text-ink dark:text-ink-dark shadow-sm ring-1 ring-black/10 dark:ring-white/10"
                        : "text-muted dark:text-muted-dark hover:text-ink dark:hover:text-ink-dark"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Archivés row — only when there's something to recover.
              The chip-orange Archive icon mirrors the badge dot on
              the kebab so the operator's eye traces dot → row. */}
          {hasArchived && (
            <div className="border-t border-black/5 dark:border-white/10">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onOpenArchived();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-ink dark:text-ink-dark hover:bg-canvas dark:hover:bg-canvas-dark/50"
              >
                <Archive className="h-4 w-4 text-chip-orange" />
                <span className="flex-1 text-left">Panneaux archivés</span>
                <span className="text-xs text-muted dark:text-muted-dark tabular-nums">
                  {archivedCount}
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
